import errorWarning from "$assets/icons/error-warning.svg?url";
import { Button } from "$components/button/button";
import { DialogContainer } from "$components/dialog/dialog-container";
import { DialogFooter } from "$components/dialog/dialog-footer";
import { DialogHeader } from "$components/dialog/dialog-header";
import { toast } from "$global-providers/toast-provider";
import { useQuerystring } from "$routes/_lib/use-querystring";
import { gql } from "@apollo/client";
import { Box, makeStyles, Typography } from "@material-ui/core";
import CheckIcon from "@material-ui/icons/Check";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { QUERY as FETCH_PATIENT_INVOICES_QUERY } from "../hooks/useFetchPatientInvoices";
import { useDeleteInvoiceMutation } from "./__generated__/DeleteInvoiceModal.gql";

const useStyles = makeStyles((theme) => ({
  modal: {
    borderRadius: theme.spacing(3),
  },
  warningMessage: {
    color: theme.palette.grey[300],
    fontWeight: 400,
    marginLeft: theme.spacing(0.5),
  },
  warningIcon: {
    marginRight: theme.spacing(2),
  },
}));

gql`
  mutation deleteInvoice($input: DeleteInvoiceInput!) {
    deleteInvoice(input: $input)
  }
`;

export const DeleteInvoiceModal: FC<{
  open: boolean;
  onClose: () => void;
  invoiceId: string;
}> = ({ open, onClose, invoiceId }) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const { patientUuid } = useQuerystring();

  const [deleteInvoice] = useDeleteInvoiceMutation({
    variables: {
      input: {
        _id: invoiceId,
      },
    },
    update: (cache, res) => {
      if (res.data.deleteInvoice) {
        try {
          const cachedInvoices = cache.readQuery({
            query: FETCH_PATIENT_INVOICES_QUERY,
            variables: { patientUuid },
          });
          if (cachedInvoices) {
            cache.writeQuery({
              query: FETCH_PATIENT_INVOICES_QUERY,
              variables: { patientUuid },

              data: {
                // @ts-expect-error legacy reason unknown
                invoices: cachedInvoices.invoices.filter(
                  (i) => i._id !== invoiceId,
                ),
              },
            });
          }
        } catch {
          // do nothing, guards against scenario where LIST_INVOICES is not in the cache
        }
      }
    },
    onError() {
      toast.error(
        formatMessage({
          id: "invoicing.deleteInvoice.error.msg",
          defaultMessage: "Failed to delete this invoice",
        }),
      );
    },
    onCompleted() {
      toast.success(
        formatMessage({
          id: "invoicing.deleteInvoice.success.msg",
          defaultMessage: "Invoice was deleted successfully",
        }),
      );
    },
  });
  const [busy, setBusy] = useState(false);

  const handleDeleteInvoice = async () => {
    setBusy(true);
    try {
      await deleteInvoice();
    } finally {
      onClose();
    }
  };

  return (
    <DialogContainer open={open} className={classes.modal}>
      <DialogHeader title="">
        <Box display="flex" alignItems="center">
          <img
            src={errorWarning}
            alt="warning"
            className={classes.warningIcon}
          />
          <Typography variant="h5">
            <FormattedMessage
              id="invoicing.delete.modal.subtitle"
              defaultMessage="Are you sure you want to delete this invoice?"
            />
          </Typography>
        </Box>
      </DialogHeader>
      <DialogFooter>
        <Button color="secondary" onClick={() => onClose()} disabled={busy}>
          <FormattedMessage
            id="invoicing.delete.cancel"
            defaultMessage="Cancel"
          />
        </Button>
        <Button
          onClick={handleDeleteInvoice}
          color="error"
          data-testid="invoicing.delete.confirm"
          startIcon={<CheckIcon />}
          loading={busy}
        >
          <FormattedMessage
            id="invoicing.delete.confirm"
            defaultMessage="Delete Invoice"
          />
        </Button>
      </DialogFooter>
    </DialogContainer>
  );
};

export default DeleteInvoiceModal;
